import { useEffect, useState } from 'react';
import {
  Alert,
  Chip,
  Paper,
  Stack,
  TablePagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
const KINDS = {
  ADDED: 'Dodana pozycja',
  REMOVED: 'Pozycja poza zakresem',
  CHANGED: 'Zmiana',
  NEW_ISSUE: 'Nowy problem',
};
// Awizacja jest zapisana jako lokalny czas magazynu, bez konwersji strefy przeglądarki.
const appointment = (position) => {
  const value = position.loadingAppointmentAt ?? position.loadingDate;
  if (!value) return 'Brak awizacji';
  const date = value.slice(0, 10).split('-').reverse().join('.');
  return position.loadingAppointmentAt
    ? `${date}, ${value.slice(11, 16)}`
    : `${date} · brak godziny`;
};
const isNextShift = (position, scope) =>
  position.scopeBucket === 'NEXT_SHIFT' ||
  Boolean(
    scope?.includeNextShift &&
      position.loadingAppointmentAt &&
      position.loadingAppointmentAt >= scope.nextStart &&
      position.loadingAppointmentAt < scope.nextEnd,
  );

export default function SimulationReportPanel({ run, transports = [] }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [changePage, setChangePage] = useState(0);
  useEffect(() => {
    setPage(0);
    setChangePage(0);
  }, [run.id, search]);
  const matches = (i) =>
    [
      i.transportNumber,
      i.deliveryNumber,
      i.positionNumber,
      i.materialNumber,
      i.description,
    ].some((v) =>
      String(v ?? '')
        .toLowerCase()
        .includes(search.trim().toLowerCase()),
    );
  const positions = transports
    .map((t) => ({
      ...t,
      positions: t.positions.filter((p) =>
        matches({ ...p, transportNumber: t.transportNumber }),
      ),
    }))
    .filter((t) => t.positions.length)
    .flatMap((t, groupIndex) =>
      t.positions.map((p, index) => ({
        ...p,
        transportNumber: t.transportNumber,
        groupIndex,
        firstInGroup: index === 0,
      })),
    );
  const changeGroups = new Map();
  (run.comparison?.changes ?? [])
    .filter((c) => c.kind !== 'RESOLVED')
    .map((c) => ({
      ...c,
      messages: c.messages.filter(
        (m) => !/^(?:Pozycja )?nie wymaga już działania[.!]?$/i.test(m.trim()),
      ),
    }))
    .filter((c) => c.messages.length > 0 && matches(c))
    .forEach((c) => {
      if (!changeGroups.has(c.transportNumber))
        changeGroups.set(c.transportNumber, []);
      changeGroups.get(c.transportNumber).push(c);
    });
  const changes = [...changeGroups.values()].flatMap((rows, groupIndex) =>
    rows.map((c, index) => ({ ...c, groupIndex, firstInGroup: index === 0 })),
  );

  const pager = (count, current, set) => (
    <TablePagination
      component="div"
      count={count}
      page={current}
      rowsPerPage={25}
      rowsPerPageOptions={[25]}
      onPageChange={(_, p) => set(p)}
      labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
    />
  );
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Co zrobić?</Typography>
        <TextField
          size="small"
          label="Filtruj działania i zmiany"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Typography variant="subtitle1">
          Działania ({positions.length} pozycji)
        </Typography>
        <TableContainer
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
        >
          <Table aria-label="Działania dla transportów" sx={{ minWidth: 850 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#eef2f6' }}>
                {[
                  'Transport',
                  'Dostawa / pozycja',
                  'Materiał',
                  'Ilość do działania',
                  'Co zrobić',
                ].map((label) => (
                  <TableCell key={label} sx={{ fontWeight: 700 }}>
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {positions.slice(page * 25, (page + 1) * 25).map((p, index) => (
                <TableRow
                  key={`${p.transportNumber}/${p.deliveryNumber}/${p.positionNumber}/${index}`}
                  data-transport-tone={p.groupIndex % 2 ? 'green' : 'white'}
                  sx={{
                    bgcolor: p.groupIndex % 2 ? '#eff8f0' : '#ffffff',
                    '& > td, & > th': {
                      verticalAlign: 'top',
                      borderTop:
                        p.firstInGroup && p.groupIndex > 0
                          ? '2px solid #a7b8ac'
                          : undefined,
                    },
                  }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                  >
                    {p.transportNumber}
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {appointment(p)}
                    </Typography>
                    {isNextShift(p, run.scope) && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="info"
                        label="Następna zmiana"
                        sx={{ mt: 0.75 }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {p.deliveryNumber}
                    <Typography variant="body2" color="text.secondary">
                      Poz. {p.positionNumber}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 190,
                      maxWidth: 300,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    <Typography fontWeight={700}>{p.materialNumber}</Typography>
                    {p.description && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {p.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {p.quantity == null
                      ? '—'
                      : `${new Intl.NumberFormat('pl-PL', {
                          maximumFractionDigits: 3,
                        }).format(p.quantity)} ${p.unit ?? ''}`}
                  </TableCell>
                  <TableCell sx={{ minWidth: 330 }}>
                    <Stack spacing={0.75}>
                      {p.actions.map((a, j) => (
                        <Typography key={j} sx={{ fontSize: '0.95rem' }}>
                          {a}
                        </Typography>
                      ))}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!positions.length && (
                <TableRow>
                  <TableCell colSpan={5}>
                    Brak działań dla wybranego zakresu.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {pager(positions.length, page, setPage)}
        <Typography variant="subtitle1">
          Co się zmieniło? ({changes.length})
        </Typography>
        {run.comparison?.preview && (
          <Alert severity="info">
            Ręczny podgląd — bez porównania i maili.
          </Alert>
        )}
        {run.comparison?.historical && (
          <Alert severity="info">Wynik historyczny — bez powiadomień.</Alert>
        )}
        {run.comparison?.baseline && <Typography>Pierwsza analiza.</Typography>}
        {!run.comparison && (
          <Typography>
            Ten wynik powstał przed wprowadzeniem porównywania zmian.
          </Typography>
        )}
        <TableContainer
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
        >
          <Table aria-label="Zmiany w transportach" sx={{ minWidth: 850 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#eef2f6' }}>
                {[
                  'Transport',
                  'Dostawa / pozycja',
                  'Materiał',
                  'Ilość',
                  'Co się zmieniło',
                ].map((label) => (
                  <TableCell key={label} sx={{ fontWeight: 700 }}>
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {changes
                .slice(changePage * 25, (changePage + 1) * 25)
                .map((c, index) => (
                  <TableRow
                    key={`${c.transportNumber}/${c.deliveryNumber}/${c.positionNumber}/${index}`}
                    data-transport-tone={c.groupIndex % 2 ? 'green' : 'white'}
                    sx={{
                      bgcolor: c.groupIndex % 2 ? '#eff8f0' : '#ffffff',
                      '& > td, & > th': {
                        verticalAlign: 'top',
                        borderTop:
                          c.firstInGroup && c.groupIndex > 0
                            ? '2px solid #a7b8ac'
                            : undefined,
                      },
                    }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                    >
                      {c.transportNumber}
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {appointment(c)}
                      </Typography>
                      {isNextShift(c, run.scope) && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="info"
                          label="Następna zmiana"
                          sx={{ mt: 0.75 }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {c.deliveryNumber}
                      <Typography variant="body2" color="text.secondary">
                        Poz. {c.positionNumber}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        minWidth: 190,
                        maxWidth: 300,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      <Typography fontWeight={700}>
                        {c.materialNumber}
                      </Typography>
                      {c.description && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {c.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {c.quantity == null
                        ? '—'
                        : `${new Intl.NumberFormat('pl-PL', {
                            maximumFractionDigits: 3,
                          }).format(c.quantity)} ${c.unit ?? ''}`}
                    </TableCell>
                    <TableCell sx={{ minWidth: 330 }}>
                      <Stack spacing={0.75}>
                        <Typography fontWeight={600}>
                          {KINDS[c.kind] ?? 'Zmiana'}
                        </Typography>
                        {c.messages.map((message, j) => (
                          <Typography key={j} sx={{ fontSize: '0.95rem' }}>
                            {message}
                          </Typography>
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              {!changes.length && (
                <TableRow>
                  <TableCell colSpan={5}>
                    Brak zmian wymagających uwagi w wybranym zakresie.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {pager(changes.length, changePage, setChangePage)}
      </Stack>
    </Paper>
  );
}
